import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

export function AIArchitectureCard() {
  const { t } = useLanguage();

  const layers = [
    {
      id: "business",
      title: t("ai.arch.layer1"),
      subtitle: t("ai.arch.layer1Sub"),
      items: [t("ai.arch.layer1.i1"), t("ai.arch.layer1.i2"), t("ai.arch.layer1.i3"), t("ai.arch.layer1.i4")],
      color: "from-violet-500/20 to-purple-500/20",
      borderColor: "border-violet-400/30",
      glowColor: "shadow-violet-500/20",
    },
    {
      id: "worker",
      title: t("ai.arch.layer2"),
      subtitle: t("ai.arch.layer2Sub"),
      items: [t("ai.arch.layer2.i1"), t("ai.arch.layer2.i2"), t("ai.arch.layer2.i3"), t("ai.arch.layer2.i4")],
      color: "from-purple-500/20 to-fuchsia-500/20",
      borderColor: "border-purple-400/30",
      glowColor: "shadow-purple-500/20",
    },
    {
      id: "foundation",
      title: t("ai.arch.layer3"),
      subtitle: t("ai.arch.layer3Sub"),
      items: [t("ai.arch.layer3.i1"), t("ai.arch.layer3.i2"), t("ai.arch.layer3.i3"), t("ai.arch.layer3.i4")],
      color: "from-fuchsia-500/20 to-pink-500/20",
      borderColor: "border-fuchsia-400/30",
      glowColor: "shadow-fuchsia-500/20",
    },
  ];

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 rounded-3xl p-6 shadow-2xl overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] bg-purple-500/10 rounded-full blur-3xl" />

      <div className="relative z-10 mb-4">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-2 py-1 bg-purple-500/20 backdrop-blur-xs rounded-full text-purple-300 text-[10px] font-medium mb-2 border border-purple-500/30"
        >
          <span className="w-1 h-1 bg-purple-400 rounded-full animate-pulse" />
          {t("ai.arch.badge")}
        </motion.div>
        <h4 className="text-base font-bold text-white">{t("ai.arch.title")}</h4>
      </div>

      <div className="relative z-10 flex gap-3">
        {layers.map((layer, index) => (
          <motion.div
            key={layer.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15 }}
            whileHover={{ scale: 1.02, y: -2 }}
            className="relative flex-1"
          >
            {index < layers.length - 1 && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-3 h-px z-0">
                <motion.div
                  className="absolute inset-0 h-[2px] -translate-y-1/2"
                  style={{ background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.6) 0%, rgba(192, 132, 252, 0.8) 50%, rgba(168, 85, 247, 0.6) 100%)' }}
                />
                <motion.div
                  className="absolute h-[2px] w-2 -translate-y-1/2 rounded-full"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.9), transparent)' }}
                  animate={{ left: ['-8px', '12px'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: "linear", delay: index * 0.4 }}
                />
              </div>
            )}

            <div className={`relative backdrop-blur-xl bg-gradient-to-b ${layer.color} rounded-xl p-3 border ${layer.borderColor} shadow-lg ${layer.glowColor} h-full min-h-[200px] flex flex-col`}>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center justify-between mb-3">
                  <motion.div
                    animate={{ boxShadow: ['0 0 10px rgba(168, 85, 247, 0.3)', '0 0 20px rgba(168, 85, 247, 0.5)', '0 0 10px rgba(168, 85, 247, 0.3)'] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-2 h-2 bg-purple-400 rounded-full"
                  />
                </div>
                
                <div className="mb-3">
                  <h5 className="text-white font-semibold text-xs leading-tight">{layer.title}</h5>
                  <p className="text-purple-300/70 text-[10px]">{layer.subtitle}</p>
                </div>

                <div className="flex flex-col gap-1.5 flex-1">
                  {layer.items.map((item, idx) => (
                    <motion.span
                      key={item}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 + idx * 0.05 }}
                      whileHover={{ x: 2 }}
                      className="px-2 py-1 bg-white/10 backdrop-blur-xs rounded text-white/80 text-[10px] border border-white/10 hover:border-purple-400/50 hover:bg-white/15 transition-all cursor-default"
                    >
                      {item}
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
        className="relative z-10 mt-4 flex items-center justify-center gap-3"
      >
        <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 backdrop-blur-xs rounded-full border border-purple-500/20">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-1 h-1 bg-purple-400 rounded-full"
                animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
          <span className="text-purple-300/80 text-[10px]">{t("ai.arch.dataFlow")}</span>
        </div>
      </motion.div>

      <div className="absolute top-3 right-3 w-12 h-12 border border-purple-500/20 rounded-full opacity-50" />
      <div className="absolute bottom-3 left-3 w-8 h-8 border border-purple-500/20 rounded-lg opacity-50 rotate-12" />
    </div>
  );
}
